import { ReplaySubject, Subscription } from 'rxjs';
import { get, isNil, keyBy, set, throttle, toPath, uniq, unset } from 'lodash';
import { SyncEvent, SyncEventStorage, SyncStorage } from './sync.model';
import { SyncService } from './sync.service';
import { mergeCopyArrays } from '@weavix/utils/src/merge-copy-arrays';
import { mongoDbMax } from '@weavix/utils/src/mongodb-max';
import { MetricAggregator } from './metric-aggregator';
import { TelemetryEvent } from '@weavix/telemetry';
import { SeqOrderBuffer } from './seq-order-buffer';

/** Base wait before a delayed partition repairs a sequence gap, so late out-of-order events can fill it first. */
export const GAP_REPAIR_GRACE_MS = 1000;
/** Upper bound of the random per-client wait added to `GAP_REPAIR_GRACE_MS`, so remaining repairs do not arrive together. */
export const GAP_REPAIR_JITTER_MS = 2000;
/** Attempts at a resubscribe pass that fails, and the wait before the second, doubled for each further one. */
const RESUBSCRIBE_ATTEMPTS = 5;
const RESUBSCRIBE_RETRY_MS = 1000;

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => { setTimeout(resolve, ms); });
}

/** An ordered partition resyncs instead of fetching once this many sequences are missing, as the legacy check does. */
const MAX_ORDERED_MISSING = 10;
/** Bounds the events an ordered partition holds behind one gap; past it the partition resyncs rather than grow. */
const MAX_ORDERED_HELD = 1000;
/** How long an ordered repair waits for its fetch before releasing the held events and leaving the gap to the legacy check. */
export const ORDERED_REPAIR_TIMEOUT_MS = 10_000;
/** Repairs in a row that leave a gap before the partition releases the held events around it. */
const MAX_ORDERED_REPAIR_ATTEMPTS = 3;
/** How long, after a repair fetch times out or fails, repairs release around a gap instead of waiting on another fetch. */
export const ORDERED_REPAIR_BACKOFF_MS = 30_000;

export interface DeduplicationTelemetry {
    topic: string;
    diffMs: number;
    winnerNetwork?: string;
    loserNetwork?: string;
    winnerNetworkType: 'mobile' | 'wifi';
    loserNetworkType: 'mobile' | 'wifi';
}

// Yield to the UI / next macrotask. Prefers setImmediate (RN polyfill),
// falls back to requestAnimationFrame (frame boundary), then setTimeout.
function yieldToUI(): Promise<void> {
    return new Promise(resolve => {
        if (typeof setImmediate === 'function') {
            // RN polyfill uses MessageChannel under the hood
            setImmediate(resolve);
        } else if (typeof document !== 'undefined' && !document.hidden && typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve());
        } else {
            // Last resort; may be throttled but still a macrotask
            setTimeout(resolve, 0);
        }
    });
}


export class SyncPartition<T extends { id?: string }> {
    readonly loaded$ = new ReplaySubject<boolean>(1);
    readonly reset$ = new ReplaySubject<boolean>(1);

    private subscription?: Subscription;
    private closed = false;
    /** The resubscribe under way, resolving to whether its last pass succeeded. */
    private resubscribing?: Promise<boolean>;
    /** Set when another resubscribe was asked for while one ran, so it runs once more instead of being dropped. */
    private resubscribeAgain = false;
    /** When the resubscribe pass under way started. */
    private resubscribeStartedAt = 0;
    private updating: Array<SyncEvent<T>> = [];
    private applying = false;
    private simulcastedEventCache: { [key: string]: { date: string, networkId?: number, networkType?: string, id?: string, networkName?: string } } = {};
    private readonly metricAggregator = new MetricAggregator<DeduplicationTelemetry>();
    private readonly missingSyncEventAggregator = new MetricAggregator<{ missingNetwork: 'mobile' | 'wifi' | 'seq' | 'unknown' }>();
    private telemetryInterval?: ReturnType<typeof setInterval>;
    private gapCheckTimer?: ReturnType<typeof setTimeout>;
    private gapCheckInFlight = false;
    private gapCheckRequestedDuringFlight = false;
    /** Set once initial sync has applied; live events then go through it, so they apply in sequence order. */
    private orderBuffer?: SeqOrderBuffer<SyncEvent<T>>;
    private highestAppliedSeq?: number;
    private orderedRepairTimer?: ReturnType<typeof setTimeout>;
    private orderedRepairInFlight = false;
    private heldSince?: number;
    private maxHeldMs = 0;
    private orderedResyncs = 0;
    private orderedDegraded = 0;
    private orderedRepairAttempts = 0;
    private orderedLateApplied = 0;
    /** After a repair fetch times out or fails, repairs skip fetching until then and release around the gap instead. */
    private orderedRepairBackoffUntil = 0;

    constructor(
        private readonly syncService: SyncService,
        private readonly storage: SyncStorage<T>,
        public readonly topic: string,
        public readonly partition: string,
        private readonly lazy: boolean,
        private readonly simulcast = false,
        private reinitialize = false,
        private readonly eventStorage?: SyncEventStorage<T> | null,
        /** Wait for late events before repairing a gap; only for shared broadcast topics, where apply order is not critical. */
        private readonly delayGapRepair = false,
    ) {
        if (this.simulcast || this.delayGapRepair || !!this.eventStorage) {
            this.telemetryInterval = setInterval(() => {
                console.log(`[Sync Partition] checking for sync telemetry...`);
                if (this.metricAggregator.dataPointCount > 0) {
                    this.syncService.sendTelemetry(TelemetryEvent.SyncDeduplicate, {
                        date: new Date(),
                        eventCount: this.metricAggregator.dataPointCount,
                        minDiffMs: this.metricAggregator.minOf('diffMs'),
                        maxDiffMs: this.metricAggregator.maxOf('diffMs'),
                        avgDiffMs: this.metricAggregator.avgOf('diffMs'),
                        wifiWins: this.metricAggregator.countOf('winnerNetworkType', 'wifi'),
                        mobileWins: this.metricAggregator.countOf('winnerNetworkType', 'mobile'),
                        winnerNetworks: this.metricAggregator.countOf('winnerNetwork'),
                        loserNetworks: this.metricAggregator.countOf('loserNetwork'),
                        topic: this.topic,
                    });
                    this.metricAggregator.clear();
                }

                if (this.missingSyncEventAggregator.dataPointCount > 0 || this.orderedResyncs > 0 || this.orderedDegraded > 0 || this.orderedLateApplied > 0 || this.maxHeldMs > 0) {
                    this.syncService.sendTelemetry(TelemetryEvent.SyncEventMissing, {
                        date: new Date(),
                        eventCount: this.missingSyncEventAggregator.dataPointCount,
                        topic: this.topic,
                        missingWifi: this.missingSyncEventAggregator.countOf('missingNetwork', 'wifi'),
                        missingMobile: this.missingSyncEventAggregator.countOf('missingNetwork', 'mobile'),
                        missingSeq: this.missingSyncEventAggregator.countOf('missingNetwork', 'seq'),
                        orderedResyncs: this.orderedResyncs,
                        orderedDegraded: this.orderedDegraded,
                        orderedLateApplied: this.orderedLateApplied,
                        maxHeldMs: this.maxHeldMs,
                    });
                    this.missingSyncEventAggregator.clear();
                    this.orderedResyncs = 0;
                    this.orderedDegraded = 0;
                    this.orderedLateApplied = 0;
                    this.maxHeldMs = 0;
                }

                const pruned = Object.values(this.simulcastedEventCache).map(x => {
                    if (Date.now() - new Date(x.date).getTime() > 120_000) {
                        this.missingSyncEventAggregator.accumulate({
                            missingNetwork: x.networkType === 'wifi' ? 'mobile' : 'wifi',
                        });
                        return undefined!;
                    }
                    return x;
                }).filter(Boolean);

                this.simulcastedEventCache = keyBy(pruned, 'id');
                if (this.simulcast) console.log('[Sync Partition] simulcast event cache pruned.  current size:', pruned.length);
            }, 60_000 * 10);
        }

    }

    async close(reset: boolean) {
        if (this.closed) return;
        this.closed = true;

        console.log(`Clearing sync partition ${this.partition}`);
        this.cancelGapCheck();
        this.cancelOrderedRepair();
        this.orderBuffer = undefined;
        this.unsubscribe();
        this.loaded$.next(true);

        if (reset) await this.eventStorage?.close(this.partition);
    }

    /** Starts the partition: subscribes and loads it, through {@link resubscribeOnce} like every later resubscribe. */
    start(): this {
        this.resubscribeOnce().catch((e: unknown) => console.error('subscribeAndGet', e));
        return this;
    }

    /**
     * Subscribes, then loads past the high-water mark and applies live events held meanwhile. A failure after subscribing
     * releases the subscription, so no live event stays held for a load that never finishes. */
    async subscribeAndGet() {
        const missing = this.reinitialize || !this.eventStorage ? [] : await this.eventStorage.getMissingSequences(this.partition);
        const { date, seq } = this.reinitialize || typeof missing === 'number' || !this.eventStorage ? { date: null, seq: null } : await this.eventStorage.getDateSequence(this.partition);
        const seqParam = this.reinitialize || typeof missing === 'number' || isNil(seq) ? null : Math.min(seq + 1, ...missing);
        this.reinitialize = false;
        const ordered = !!this.eventStorage;
        this.orderBuffer = undefined;
        this.cancelOrderedRepair();
        this.highestAppliedSeq = isNil(seq) ? undefined : seq;

        let queue: Array<SyncEvent<T>> | null = [];

        console.log(`Initializing sync partition ${this.topic} from date ${date} and sequence ${seqParam}`);
        await this.storage.dump(this.partition);

        // Silent: storage is unchanged, so a reload here would only repeat the last one; showPartition below reloads if init wrote anything.
        this.storage.addPartition(this.partition, false);

        const subject = await this.syncService.subscribe<Array<SyncEvent<T>>>(this.topic, () => this.closed, this.lazy, this.simulcast);

        // Batch updates to prevent blocking the main thread
        const updateQueue: Array<SyncEvent<T> & { networkId: number, networkType: string }> = [];
        let isProcessing = false;

        const executeFlush = () => {
            if (isProcessing || updateQueue.length === 0) return;

            isProcessing = true;
            const toProcess = updateQueue.splice(0);

            // Yield to UI before processing to avoid blocking frames
            void (async () => {
                await yieldToUI();
                try {
                    if (queue) queue.push(...toProcess);
                    else await this.acceptLive(toProcess);
                } catch (e) {
                    console.error('Failed to process batched updates:', e);
                } finally {
                    isProcessing = false;
                    // Check if more items were added while processing
                    if (updateQueue.length > 0) {
                        throttledFlushUpdates();
                    }
                }
            })();
        };

        // Use lodash throttle for reliable throttling behavior
        const throttledFlushUpdates = throttle(executeFlush, 50, {
            leading: false,
            trailing: true,
        });

        this.subscription = subject.subscribe({
            next: val => {
                const newPayload = val.payload.map(p => ({ ...p, networkId: val.networkId, networkType: val.networkType }));
                updateQueue.push(...newPayload);
                throttledFlushUpdates();
            },
            error: () => {
                this.cancelGapCheck();
                this.resubscribeOnce().catch(e => console.error('resubscribe', e));
            },
        });

        try {
            const data = await this.syncService.get<Array<SyncEvent<T>>>(
                `/sync/${this.topic}`,
                date ? { date, seq: seqParam } : {},
                () => this.closed,
                this.lazy,
            );
            this.syncService.reportPartitionMetric(data.length, JSON.stringify(data).length);

            // suppress topic change events while processing initial data, to avoid emitting intermediate states
            this.storage.hidePartition(this.partition);

            await this.queueUpdate(data, true);
            const remaining = queue;
            queue = null;
            await this.queueUpdate(remaining, true);
            if (ordered && !this.closed) this.orderBuffer = new SeqOrderBuffer(isNil(this.highestAppliedSeq) ? undefined : this.highestAppliedSeq + 1);

            // Reloads queries only if init wrote anything; an unchanged partition (e.g. a reconnect with nothing new) costs no reload.
            this.storage.showPartition(this.partition);
        } catch (e) {
            this.subscription?.unsubscribe();
            throw e;
        }
        this.loaded$.next(true);
    }

    async flush() {
        await this.storage.flush();
    }

    private unsubscribe() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
        if (this.telemetryInterval) {
            clearInterval(this.telemetryInterval);
            this.telemetryInterval = undefined;
        }
    }

    async queueUpdate(publish: Array<SyncEvent<T>>, init: boolean) {
        if (init && publish.length) console.log(`Applying ${publish.length} events to ${this.topic}`);
        this.updating.push(...publish);
        if (this.applying) return;
        this.applying = true;
        const start = Date.now();
        let index = 0;

        // Process events in chunks to prevent blocking the main thread
        // Use larger chunks for init (thousands of events) vs smaller for real-time updates
        const CHUNK_SIZE = init ? 100 : 5; // Larger chunks during initialization

        while (index < this.updating.length && !this.closed) {
            const endIndex = Math.min(index + CHUNK_SIZE, this.updating.length);

            // Process a chunk of events
            for (let i = index; i < endIndex; i++) {
                const event = this.updating[i];
                const updateWait = this.applyUpdate(event, init);
                // Doing large amounts of promises is slow in react native... has to do with timeouts and stuff
                // So we only await when we need to (most stuff in lazy storage needs loaded once then is fine)
                if ((updateWait as Promise<unknown>)?.then) await updateWait;
                const eventWait = this.eventStorage?.add(this.partition, event.date, event.syncId, event.seq, event.recordEvent !== false);
                if ((eventWait as Promise<unknown>)?.then) await eventWait;
                if (!isNil(event.seq) && event.recordEvent !== false && (isNil(this.highestAppliedSeq) || event.seq > this.highestAppliedSeq)) {
                    this.highestAppliedSeq = event.seq;
                }
            }

            index = endIndex;

            // Yield control back to the event loop after processing each chunk
            if (index < this.updating.length) {
                await yieldToUI();
            }
        }

        this.updating = this.updating.slice(index);
        if (init && publish.length) console.log(`Done applying updates ${this.topic} in ${Date.now() - start} ms sync time ${publish[publish.length - 1].date}`);
        this.applying = false;
        if (publish.length > 0) await this.requestMissingSequenceCheck(init);
    }

    /**
     * Checks for sequence gaps now, or, for live events on a delayed partition, once after the grace period.
     * One delayed check is pending at a time; initial sync always checks immediately, matching the Kotlin engine.
     */
    private async requestMissingSequenceCheck(init: boolean) {
        if (!this.delayGapRepair || init) {
            await this.checkMissingSequences();
            return;
        }
        if (this.gapCheckInFlight) {
            this.gapCheckRequestedDuringFlight = true;
            return;
        }
        if (this.gapCheckTimer) return;
        // eslint-disable-next-line sonarjs/pseudo-random -- load-spreading jitter, not a security value
        const delayMs = GAP_REPAIR_GRACE_MS + Math.floor(Math.random() * GAP_REPAIR_JITTER_MS);
        this.gapCheckTimer = setTimeout(() => {
            this.gapCheckTimer = undefined;
            if (!this.closed) this.runDelayedGapCheck().catch((e: unknown) => console.error('runDelayedGapCheck', e));
        }, delayMs);
    }

    /** Runs one delayed gap check; a batch that lands meanwhile gets a fresh delayed check once this one settles, never an overlapping one. */
    private async runDelayedGapCheck() {
        this.gapCheckInFlight = true;
        try {
            await this.checkMissingSequences();
        } catch (e) {
            console.error(`Delayed gap check failed for sync partition ${this.topic} (${this.partition})`, e);
        } finally {
            this.gapCheckInFlight = false;
        }
        if (this.gapCheckRequestedDuringFlight && !this.closed) {
            this.gapCheckRequestedDuringFlight = false;
            await this.requestMissingSequenceCheck(false);
        }
    }

    private cancelGapCheck() {
        clearTimeout(this.gapCheckTimer);
        this.gapCheckTimer = undefined;
        this.gapCheckRequestedDuringFlight = false;
    }

    /** Live events: straight to apply, or, once ordered apply is set up, through the sequence-order buffer. */
    private async acceptLive(events: Array<SyncEvent<T>>) {
        const buffer = this.orderBuffer;
        if (!buffer) {
            await this.queueUpdate(events, false);
            return;
        }
        const ready: Array<SyncEvent<T>> = [];
        for (const event of events) {
            if (buffer.isBehind(event)) {
                // Below the watermark goes to applyUpdate as before: a copy already applied (the losing simulcast copy) only
                // records dedup telemetry, while one never applied (a gap released around, arriving late) fills its gap.
                const applied = !!event.syncId && !!this.eventStorage?.isDuplicate(event.date, event.syncId);
                if (!applied) this.orderedLateApplied++;
                ready.push(event);
            } else {
                ready.push(...buffer.accept(event));
            }
        }
        this.trackHeld(buffer);
        if (ready.length) await this.queueUpdate(ready, false);
        if (buffer.heldCount >= MAX_ORDERED_HELD) {
            this.resyncOrdered(buffer, 'too many events held');
            return;
        }
        if (buffer.heldCount) this.scheduleOrderedRepair();
    }

    /** One pending repair at a time, after the same grace and jitter as a delayed gap check. */
    private scheduleOrderedRepair() {
        if (this.orderedRepairTimer || this.orderedRepairInFlight || this.closed) return;
        // eslint-disable-next-line sonarjs/pseudo-random -- load-spreading jitter, not a security value
        const delayMs = GAP_REPAIR_GRACE_MS + Math.floor(Math.random() * GAP_REPAIR_JITTER_MS);
        this.orderedRepairTimer = setTimeout(() => {
            this.orderedRepairTimer = undefined;
            if (!this.closed) this.runOrderedRepair().catch((e: unknown) => console.error('runOrderedRepair', e));
        }, delayMs);
    }

    /**
     * Fetches from the next expected sequence and applies it merged with the held events, in sequence order. A gap of
     * `MAX_ORDERED_MISSING` resyncs; a failed or slow fetch, or repeated repairs that leave the gap, release around it. */
    private async runOrderedRepair() {
        const buffer = this.orderBuffer;
        if (!buffer?.heldCount || isNil(buffer.nextSeq)) return;
        if (buffer.missingCount >= MAX_ORDERED_MISSING) {
            this.resyncOrdered(buffer, 'gap too wide');
            return;
        }
        if (Date.now() < this.orderedRepairBackoffUntil) {
            this.releaseHeldSkippingGap(buffer, '/sync recently failed');
            return;
        }
        this.orderedRepairInFlight = true;
        try {
            const missingBefore = buffer.missingCount;
            let abandoned = false;
            const data = await this.fetchWithin<Array<SyncEvent<T>>>(this.syncService.get<Array<SyncEvent<T>>>(
                `/sync/${this.topic}`, { seq: buffer.nextSeq }, () => this.closed || abandoned, this.lazy,
            ), ORDERED_REPAIR_TIMEOUT_MS);
            if (data === undefined) {
                abandoned = true;
                this.orderedRepairBackoffUntil = Date.now() + ORDERED_REPAIR_BACKOFF_MS;
                if (!this.closed && buffer === this.orderBuffer) this.releaseHeldSkippingGap(buffer, 'repair fetch timed out');
            } else if (!this.closed && buffer === this.orderBuffer) {
                // A partition that re-initialized during the fetch has a new buffer; the reschedule below covers it.
                this.orderedRepairBackoffUntil = 0;
                data.forEach(event => event.fetchedForRepair = true);
                for (let i = 0; i < missingBefore; i++) this.missingSyncEventAggregator.accumulate({ missingNetwork: 'seq' });
                const { ready, stillMissing } = buffer.merge(data);
                this.trackHeld(buffer);
                if (ready.length) await this.queueUpdate(ready, false);
                this.orderedRepairAttempts = stillMissing ? this.orderedRepairAttempts + 1 : 0;
                if (stillMissing && this.orderedRepairAttempts >= MAX_ORDERED_REPAIR_ATTEMPTS) {
                    this.orderedRepairAttempts = 0;
                    this.releaseHeldSkippingGap(buffer, `${MAX_ORDERED_REPAIR_ATTEMPTS} repairs did not fill the gap`);
                }
            }
        } catch (e) {
            console.error(`Ordered gap repair failed for sync partition ${this.topic} (${this.partition})`, e);
            this.orderedRepairBackoffUntil = Date.now() + ORDERED_REPAIR_BACKOFF_MS;
            if (!this.closed && buffer === this.orderBuffer) this.releaseHeldSkippingGap(buffer, 'repair fetch failed');
        } finally {
            this.orderedRepairInFlight = false;
        }
        if (this.orderBuffer?.heldCount) this.scheduleOrderedRepair();
    }

    /** The fetch's result, or undefined if it has not settled within `timeoutMs`; a late result is ignored. */
    private async fetchWithin<R>(fetch: Promise<R>, timeoutMs: number): Promise<R | undefined> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<undefined>(resolve => { timer = setTimeout(() => resolve(undefined), timeoutMs); });
        try {
            return await Promise.race([fetch, timeout]);
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * Degrades rather than hiding live events behind a gap it cannot repair: applies the held events in order and leaves the
     * gap to the legacy check. Not awaited, so a legacy fetch waiting on `/sync` never blocks the next ordered repair. */
    private releaseHeldSkippingGap(buffer: SeqOrderBuffer<SyncEvent<T>>, reason: string) {
        console.log(`SyncPartition ${this.topic}: ${reason}, applying ${buffer.heldCount} held events and leaving the gap to the legacy check`);
        this.orderedDegraded++;
        this.trackHeld(buffer, true);
        const released = buffer.releaseAllSkippingGap();
        if (released.length) this.queueUpdate(released, false).catch((e: unknown) => console.error('releaseHeldSkippingGap', e));
    }

    private resyncOrdered(buffer: SeqOrderBuffer<SyncEvent<T>>, reason: string) {
        console.log(`SyncPartition resyncing ${this.topic}: ordered repair ${reason} (${buffer.missingCount} missing, ${buffer.heldCount} held)`);
        this.orderedResyncs++;
        this.trackHeld(buffer, true);
        this.resubscribeAndGet(buffer.missingCount);
    }

    /** Tracks how long the oldest held event has waited, for the `maxHeldMs` telemetry. */
    private trackHeld(buffer: SeqOrderBuffer<SyncEvent<T>>, ending = false) {
        const now = Date.now();
        if (this.heldSince !== undefined) this.maxHeldMs = Math.max(this.maxHeldMs, now - this.heldSince);
        if (ending || !buffer.heldCount) this.heldSince = undefined;
        else this.heldSince ??= now;
    }

    private cancelOrderedRepair() {
        clearTimeout(this.orderedRepairTimer);
        this.orderedRepairTimer = undefined;
    }

    private async checkMissingSequences() {
        if (!this.eventStorage) return;

        const missing = await this.eventStorage.getMissingSequences(this.partition);

        if (typeof missing === 'number') {
            this.resubscribeAndGet(missing);
            return;
        }

        if (!missing?.length) {
            await this.eventStorage.prune(this.partition);
            return;
        }

        const seq = Math.min(...missing);
        const data = await this.syncService.get<Array<SyncEvent<T>>>(
            `/sync/${this.topic}`, { seq }, () => this.closed, this.lazy,
        );
        console.log(`SyncPartition got missing data ${this.topic}: ${data.length}`);
        data.forEach(syncEvent => syncEvent.forceProcess = true);

        for (const _ of data) {
            this.missingSyncEventAggregator.accumulate({ missingNetwork: 'seq' });
        }

        const dataSequences = new Set(data.map(x => x.seq));
        const stillMissing = missing.filter(x => !dataSequences.has(x));

        if (stillMissing.length > 0) {
            console.log(`SyncPartition resyncing because of missing sequences ${this.topic}: ${stillMissing.length}`);
            this.resubscribeAndGet(data.length - missing.length);
            return;
        }
        await this.queueUpdate(data, false);
    }

    /**
     * After MQTT moved to another broker at `movedAt`, resubscribes and fetches past the high-water mark, unless a pass that
     * started since already does. Resolves to success, or undefined once closed. */
    async catchUp(movedAt: number): Promise<boolean | undefined> {
        if (this.closed) return undefined;
        if (this.resubscribing !== undefined && this.resubscribeStartedAt >= movedAt) return await this.resubscribing;
        console.log(`SyncPartition catching up ${this.topic} after a broker move`);
        this.cancelGapCheck();
        this.cancelOrderedRepair();
        this.orderBuffer = undefined;
        return await this.resubscribeOnce();
    }

    /** Resubscribes and fetches from the high-water mark; a request while one runs makes it run once more, never alongside. */
    private resubscribeOnce(): Promise<boolean> {
        if (this.resubscribing !== undefined) {
            this.resubscribeAgain = true;
            return this.resubscribing;
        }
        this.resubscribing = this.runResubscribes();
        return this.resubscribing;
    }

    /** Runs resubscribe passes until none is pending, retrying a failed one up to {@link RESUBSCRIBE_ATTEMPTS} times with backoff. */
    private async runResubscribes(): Promise<boolean> {
        let succeeded = false;
        let attempt = 0;
        do {
            this.resubscribeAgain = false;
            this.resubscribeStartedAt = Date.now();
            this.subscription?.unsubscribe();
            try {
                await this.subscribeAndGet();
                succeeded = true;
                attempt = 0;
            } catch (e) {
                succeeded = false;
                attempt++;
                console.error(`SyncPartition could not resubscribe ${this.topic} (attempt ${attempt})`, e);
                if (attempt < RESUBSCRIBE_ATTEMPTS && !this.closed) {
                    await sleep(RESUBSCRIBE_RETRY_MS * 2 ** (attempt - 1));
                    this.resubscribeAgain = true;
                }
            }
        } while (this.resubscribeAgain && !this.closed);
        this.resubscribing = undefined;
        return succeeded;
    }

    /** Resyncs from scratch, through {@link resubscribeOnce} so it never runs alongside another resubscribe. */
    resubscribeAndGet(missing?: number) {
        if (missing) console.log(`SyncPartition resyncing because of missing sequences ${this.topic}: ${missing}`);
        else console.log(`SyncPartition resyncing ${this.topic}`);
        this.cancelGapCheck();
        this.cancelOrderedRepair();
        this.orderBuffer = undefined;
        this.reinitialize = true;
        this.resubscribeOnce().catch(e => console.error('subscribeAndGet', e));
    }

    // eslint-disable-next-line complexity
    protected applyUpdate(publish: SyncEvent<T>, init: boolean) {
        if (publish.syncId && this.eventStorage?.isDuplicate(publish.date, publish.syncId, publish.fetchedForRepair) && !publish.forceProcess) {
            if (!init) {
                console.log(`Sync ${publish.syncId} ignored due to duplication for ${this.topic}`);
                const winner = this.simulcastedEventCache[publish.syncId];
                const network = this.syncService.getCurrentNetworks()[publish.networkId!];
                if (winner) {
                    const winnerDate = new Date(winner.date);
                    const loserDate = new Date();
                    this.metricAggregator.accumulate({
                        topic: this.topic,
                        diffMs: loserDate.getTime() - winnerDate.getTime(),
                        winnerNetwork: winner.networkName,
                        loserNetwork: network?.carrier ?? network?.ssid,
                        winnerNetworkType: winner.networkType as 'mobile' | 'wifi',
                        loserNetworkType: publish.networkType as 'mobile' | 'wifi',
                    });
                    delete this.simulcastedEventCache[publish.syncId];
                }
            }
            return;
        }
        if (this.simulcast && !init && Object.keys(this.syncService.getCurrentNetworks()).length > 1) {
            const network = this.syncService.getCurrentNetworks()[publish.networkId!];
            this.simulcastedEventCache[publish.syncId!] = {
                date: new Date().toISOString(),
                networkId: publish.networkId,
                networkType: publish.networkType,
                id: publish.syncId,
                networkName: network?.carrier ?? network?.ssid,
            };
            console.log(`Sync ${publish.syncId} added to cache for ${this.topic}`);
        }
        switch (publish.type) {
            case 'reset':
                console.log(`Sync reset event for ${this.topic}`);
                return this.reset();
            case 'clear':
                console.log(`Sync clear event for ${this.topic} of size ${publish.payload.length}`);
                return this.eventStorage?.clear(this.partition, publish.payload ?? [], publish.seq ?? 0);
            case 'insert':
                if (!init) console.log(`Sync insert event for ${this.partition} id ${publish.payload.id}`);
                return this.storage.add(publish.payload, this.partition);
            case 'delete':
            case 'update':
            case 'pull':
            case 'push': {
                if (!init) console.log(`Sync ${publish.type} event for ${this.partition} from ${this.topic} id ${publish.id}`);

                const getPayloadKeys = () => uniq(Object.keys('payload' in publish ? publish.payload ?? {} : {}).map(k => toPath(k)[0]));
                switch (publish.type) {
                    case 'delete':
                        return this.storage.remove(publish.id, this.partition);
                    case 'update':
                        if (publish.inc || publish.set || publish.max || publish.unset) {
                            const updateRecord = (record: T) => {
                                if (publish.inc) {
                                    for (const [field, incValue] of Object.entries(publish.inc)) {
                                        const current = get(record, field) as number ?? 0;
                                        const newValue = current + incValue as number;
                                        set(record, field, newValue);
                                    }
                                }
                                if (publish.max) {
                                    for (const [field, maxValue] of Object.entries(publish.max)) {
                                        const current = get(record, field) as number ?? 0;
                                        const newValue = mongoDbMax(current, maxValue as number);
                                        set(record, field, newValue);
                                    }
                                }
                                if (publish.set) {
                                    for (const [field, setValue] of Object.entries(publish.set)) {
                                        set(record, field, setValue);
                                    }
                                }
                                if (publish.unset) {
                                    for (const field of Object.keys(publish.unset)) {
                                        unset(record, field);
                                    }
                                }
                                return record;
                            };
                            // payload may not be populated; determine keys from operators.
                            const getKeys = () => {
                                const getOpKeys = (op: unknown) => Object.keys(op ?? {}).map(k => toPath(k)[0]);
                                return uniq([
                                    ...getOpKeys(publish.inc),
                                    ...getOpKeys(publish.set),
                                    ...getOpKeys(publish.max),
                                    ...getOpKeys(publish.unset),
                                ]);
                            };
                            return this.storage.update(publish.id, updateRecord, getKeys, this.partition);
                        } else {
                            return this.storage.update(publish.id, record => mergeCopyArrays(record, publish.payload), getPayloadKeys, this.partition);
                        }
                    case 'pull': {
                        const pull = (record: T) => {
                            Object.keys(publish.payload).forEach(field => {
                                const array = get(record, field) as unknown;
                                if (array && Array.isArray(array)) {
                                    const filter = publish.payload[field as keyof T];
                                    for (let i = 0; i < array.length; i++) {
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
                                        if (typeof filter === 'object' && Object.keys(filter).every(key => array[i][key] === (filter as any)[key])
                                            || typeof filter !== 'object' && filter === array[i]) {
                                            array.splice(i--, 1);
                                        }
                                    }
                                }
                            });
                            return record;
                        };
                        return this.storage.update(publish.id, pull, getPayloadKeys, this.partition);
                    }
                    case 'push': {
                        const push = (record: T) => {
                            Object.keys(publish.payload).forEach(field => {
                                const array = get(record, field) as unknown;
                                if (!array) set(record, field, [publish.payload[field as keyof T]]);
                                else if (Array.isArray(array)) array.push(publish.payload[field as keyof T]);
                            });
                            return record;
                        };
                        return this.storage.update(publish.id, push, getPayloadKeys, this.partition);
                    }
                }
                /* FALLTHROUGH */
            }
            default:
                console.warn(`${(publish as SyncEvent<T>).type} sync event not recognized`);
                return;
        }
    }

    private async reset() {
        await this.close(false);
        this.reset$.next(true);
    }
}
